import { waPhone } from "@/lib/phone";
import { useLive } from "@/lib/live";
import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import NewOrderForm from "@/components/NewOrderForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ItemPicker from "@/components/ItemPicker";
import {
  FileText, Plus, Trash, WhatsappLogo, DownloadSimple, ArrowRight, PencilSimple, QrCode,
  User, Phone as PhoneIcon, Calculator, X, CheckCircle
} from "@phosphor-icons/react";
import { toast } from "sonner";

const THICKNESSES = ["1mm", "1.5mm", "2mm", "2.5mm", "3mm", "4mm", "5mm", "6mm", "8mm", "10mm"];
const marginForItemName = (name = "") =>
  /\bSS\b|stainless/i.test(String(name)) ? 6 : 3;
const parseThickness = (name = "") => {
  const m = String(name).match(/(\d+(?:\.\d+)?)\s*mm/i);
  return m ? `${m[1]}mm` : null;
};
const snapMargin = (v, step = 3) => {
  const n = Number(v) || 0;
  const s = Number(step) || 3;
  if (n <= 0) return 0;
  return Math.ceil(n / s) * s;
};
const blankItem = () => ({
  item_id: "",
  item_name: "",
  thickness: "2mm",
  rate_per_sqft: 250,
  width_in: 48,
  height_in: 96,
  quantity: 1,
  sizing_mode: "standard",
  unit: "sqft",
  category: "sheet",
  margin_inches: 3,
  margin_w_inches: 3,
  margin_h_inches: 3,
  discount_amount: 0,
});
const snap3 = (v) => {
  const n = Number(v) || 0;
  if (n <= 0) return 0;
  return Math.ceil(n / 3) * 3;
};
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const r3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;

export default function EstimatesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [convertingId, setConvertingId] = useState(null);
  const [editing, setEditing] = useState(null); // full estimate doc

  useLive(() => { load(); });
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/estimates");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const share = (est) => {
    const text =
      `*Shobhit Creation — Quotation*\n\n` +
      `Namaste ${est.customer_name} ji\n` +
      `SqFt: ${est.total_sqft}\n` +
      `Estimated Amount: ₹${est.total_amount.toLocaleString("en-IN")}\n\n` +
      `Advance lekar order confirm karo. — Shobhit Creation`;
    const m = String(est.mobile || "").replace(/\D/g, "");
    const phone = waPhone(m);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank");
  };

  const downloadPdf = (est) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/estimates/${est.id}/pdf?auth=${localStorage.getItem("sc_token") || ""}`;
    window.open(url, "_blank");
  };

  const removeEst = async (id) => {
    if (!window.confirm("Delete this estimate?")) return;
    try {
      await api.delete(`/estimates/${id}`);
      toast.success("Estimate deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="estimates-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-mono-num tracking-widest text-slate-400">// QUOTATIONS</div>
          <h1 className="font-heading text-3xl font-black text-white">Estimates</h1>
          <p className="text-slate-400 text-sm mt-1">
            Quotations you can share with customers. Convert to a live order once they say yes.
          </p>
        </div>
        <Button
          onClick={() => setFormOpen(true)}
          className="btn-industrial rounded-sm h-11 px-5 font-heading tracking-widest"
          data-testid="new-estimate-btn"
        >
          <Plus size={16} className="mr-1" weight="bold" /> NEW ESTIMATE
        </Button>
      </div>

      <div className="surface rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-800 text-[10px] font-mono-num tracking-widest text-slate-500">
          <div className="col-span-3">EST# / CUSTOMER</div>
          <div className="col-span-2">MOBILE</div>
          <div className="col-span-2 text-right">SQFT</div>
          <div className="col-span-2 text-right">AMOUNT</div>
          <div className="col-span-1">STATUS</div>
          <div className="col-span-2 text-right">ACTIONS</div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <FileText size={40} className="mx-auto mb-3 text-slate-700" />
            No estimates yet. Tap <b className="text-orange-400">+ NEW ESTIMATE</b> to draft one.
          </div>
        ) : rows.map((e) => (
          <div key={e.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-800 hover:bg-slate-800/40 items-center" data-testid={`estimate-row-${e.id}`}>
            <div className="col-span-3">
              <div className="font-mono-num text-orange-400 text-sm font-bold truncate" data-testid={`estimate-no-${e.id}`}>
                {e.estimate_number || e.order_number || "—"}
              </div>
              <div className="text-slate-200 truncate text-sm">{e.customer_name}</div>
              <div className="text-[10px] font-mono-num text-slate-500 mt-0.5">
                {new Date(e.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
              </div>
            </div>
            <div className="col-span-2 font-mono-num text-cyan-300 text-sm">{e.mobile}</div>
            <div className="col-span-2 text-right font-mono-num text-slate-200">{e.total_sqft}</div>
            <div className="col-span-2 text-right font-mono-num text-orange-400 font-bold">
              ₹{e.total_amount.toLocaleString("en-IN")}
            </div>
            <div className="col-span-1">
              {e.status === "converted" ? (
                <span className="text-[10px] font-mono-num text-emerald-400 border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 rounded-sm">
                  CONVERTED
                </span>
              ) : (
                <span className="text-[10px] font-mono-num text-amber-300 border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 rounded-sm">
                  PENDING
                </span>
              )}
            </div>
            <div className="col-span-2 flex items-center gap-1 justify-end">
              <Button
                onClick={() => downloadPdf(e)}
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0 border-slate-600 text-slate-300 hover:bg-slate-800"
                data-testid={`estimate-pdf-${e.id}`}
                title="Preview PDF"
              >
                <DownloadSimple size={14} />
              </Button>
              <Button
                onClick={() => share(e)}
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                data-testid={`estimate-wa-${e.id}`}
                title="Share on WhatsApp"
              >
                <WhatsappLogo size={14} weight="fill" />
              </Button>
              {e.status !== "converted" && (
                <Button
                  onClick={() => setEditing(e)}
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0 border-orange-500/40 text-orange-300 hover:bg-orange-500/10"
                  data-testid={`estimate-edit-${e.id}`}
                  title="Edit — name, mobile, address, items, rate, margin"
                >
                  <PencilSimple size={14} />
                </Button>
              )}
              {e.status !== "converted" ? (
                <Button
                  onClick={() => setConvertingId(e.id)}
                  size="sm"
                  className="h-8 px-2 rounded-sm bg-orange-500 hover:bg-orange-400 text-white font-mono-num text-[11px] tracking-widest"
                  data-testid={`estimate-convert-${e.id}`}
                >
                  CONVERT <ArrowRight size={12} className="ml-1" />
                </Button>
              ) : (
                <span className="text-[10px] text-emerald-400 px-2 flex items-center gap-1">
                  <CheckCircle size={12} weight="fill" /> Order
                </span>
              )}
              <Button
                onClick={() => removeEst(e.id)}
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0 border-red-500/40 text-red-400 hover:bg-red-500/10"
                data-testid={`estimate-delete-${e.id}`}
                title="Delete"
              >
                <Trash size={14} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <EstimateFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onCreated={() => { setFormOpen(false); load(); }}
      />

      <EstimateEditDialog
        estimate={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />

      <ConvertDialog
        estimateId={convertingId}
        estimateRow={rows.find((r) => r.id === convertingId) || null}
        onClose={() => setConvertingId(null)}
        onConverted={() => { setConvertingId(null); load(); }}
      />
    </div>
  );
}

/* ---------------- New Estimate Form Dialog ---------------- */
// Reuses the full NewOrderForm component in `mode="estimate"` so the
// Estimate creation experience is IDENTICAL to New Order (per user brief:
// "same design bass NEW ORDER ki jagah ESTIMATE likh do"). Submits to
// /estimates instead of /orders. Advance/fabricator flow is skipped for
// estimates — that kicks in once the estimate is "Converted to Order".
function EstimateFormDialog({ open, onClose, onCreated }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-950 border-slate-700 rounded-sm w-[96vw] max-w-6xl h-[92vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-5 py-3 border-b border-slate-800 flex-shrink-0">
          <DialogTitle className="text-white font-heading tracking-widest flex items-center gap-2">
            <FileText size={20} className="text-orange-400" />
            NEW ESTIMATE
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-5">
          <NewOrderForm
            mode="estimate"
            onCreated={(data) => onCreated?.(data)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* ---------------- Edit Estimate Dialog ---------------- */
// Reuses NewOrderForm in edit mode with editKind="estimate" so all fields
// (customer, mobile, address, items, size, rate, margin, notes) can be
// modified. Submits via PATCH /estimates/{id}. Blocked once the estimate
// has been converted (see backend).
function EstimateEditDialog({ estimate, onClose, onSaved }) {
  const open = !!estimate;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-slate-950 border-slate-700 rounded-sm w-[96vw] max-w-6xl h-[92vh] p-0 overflow-hidden flex flex-col"
        data-testid="estimate-edit-dialog"
      >
        <DialogHeader className="px-5 py-3 border-b border-slate-800 flex-shrink-0">
          <DialogTitle className="text-white font-heading tracking-widest flex items-center gap-2">
            <PencilSimple size={20} className="text-orange-400" />
            EDIT ESTIMATE {estimate?.estimate_number || ""}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-5">
          {estimate && (
            <NewOrderForm
              mode="estimate"
              editKind="estimate"
              initialOrder={estimate}
              onSaved={(data) => onSaved?.(data)}
              onCancelEdit={onClose}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* ---------------- Convert dialog ---------------- */
function ConvertDialog({ estimateId, onClose, onConverted, estimateRow }) {
  const [advance, setAdvance] = useState("");
  const [mode, setMode] = useState("cash");
  const [modes, setModes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [upiId, setUpiId] = useState("");
  const [upiModeKey, setUpiModeKey] = useState("");  // payment_mode key that owns the UPI ID
  const [payeeName, setPayeeName] = useState("Shobhit Creation");
  const open = !!estimateId;

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/settings/payment-modes");
        setModes(data || []);
        if (data?.length) setMode(data[0].key);
        // Prefer a mode marked `is_customer_default` with a upi_id;
        // fall back to any mode with a upi_id configured. This is the
        // one whose account the customer will be paying INTO.
        const preferred = (data || []).find(
          (m) => (m.upi_id || "").trim() && m.is_customer_default
        ) || (data || []).find((m) => (m.upi_id || "").trim());
        if (preferred) {
          setUpiId(preferred.upi_id);
          setUpiModeKey(preferred.key);
        }
      } catch (_e) { /* ignore */ }
      try {
        const { data } = await api.get("/settings/business");
        // Business-level UPI ID acts as a fallback when no payment mode
        // has one configured.
        if (data?.upi_id && !upiId) setUpiId(data.upi_id);
        if (data?.business_name) setPayeeName(data.business_name);
      } catch (_e) { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => { if (!open) { setAdvance(""); setShowQR(false); } }, [open]);

  const amt = Number(advance) || 0;
  const isOnline = mode === "__upi__";
  const estNoForNote = estimateRow?.estimate_number || estimateRow?.order_number || "";
  const upiUri = upiId && amt > 0
    ? `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(payeeName)}&am=${amt}&cu=INR&tn=${encodeURIComponent("Advance-" + estNoForNote)}`
    : "";
  const qrUrl = upiUri
    ? `https://api.qrserver.com/v1/create-qr-code/?size=260x260&margin=8&data=${encodeURIComponent(upiUri)}`
    : "";

  const submit = async (e) => {
    e.preventDefault();
    if (isOnline && amt > 0 && !upiId) {
      return toast.error("UPI ID Settings → Business Info me add karo");
    }
    // For online mode we submit under the real payment_mode key that owns
    // the UPI ID (backend validates against db.payment_modes). If none is
    // configured, fall back to the currently-selected mode.
    const submitMode = isOnline
      ? (upiModeKey || mode)
      : mode;
    setBusy(true);
    try {
      const { data } = await api.post(`/estimates/${estimateId}/convert`, {
        advance_amount: amt,
        advance_mode: amt > 0 ? submitMode : null,
      });
      toast.success(`Estimate converted → Order #${data.order_number}`);
      onConverted?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-md bg-slate-900 border-slate-700 text-white rounded-sm"
        data-testid="estimate-convert-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <ArrowRight size={20} className="text-orange-400" /> Convert to Order
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="text-sm text-slate-400">
            This will create a live order from this estimate. Advance optional — for online, generate QR aur customer se scan karwao.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">ADVANCE (OPTIONAL)</Label>
              <Input
                type="number" min="0" step="0.01"
                value={advance} onChange={(e) => { setAdvance(e.target.value); setShowQR(false); }}
                placeholder="0"
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm font-mono-num mt-1.5"
                data-testid="convert-advance-input"
              />
            </div>
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">MODE</Label>
              <Select value={mode} onValueChange={(v) => { setMode(v); setShowQR(false); }}>
                <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5" data-testid="convert-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-600 text-white">
                  {modes.map((m) => (
                    <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                  ))}
                  <SelectItem value="__upi__">Online (UPI QR)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {isOnline && amt > 0 && (
            <div className="border border-cyan-500/30 rounded-sm bg-cyan-500/5 p-3 space-y-2" data-testid="convert-qr-panel">
              {!showQR ? (
                <Button type="button"
                  onClick={() => setShowQR(true)}
                  disabled={!upiId}
                  className="w-full h-11 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-heading tracking-widest"
                  data-testid="convert-generate-qr">
                  <QrCode size={16} className="mr-2" weight="bold" />
                  {upiId ? `GENERATE QR · ₹${amt}` : "UPI ID missing → Settings → Business Info"}
                </Button>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <img src={qrUrl} alt="UPI QR" width={220} height={220}
                    className="rounded-sm bg-white p-1" data-testid="convert-qr-image" />
                  <div className="text-[11px] font-mono-num text-slate-400">
                    ₹{amt} · {payeeName} · {upiId}
                  </div>
                  <div className="text-[10px] text-slate-500 text-center">
                    Customer PhonePe/GPay/Paytm se scan kare. Payment aane ke baad neeche "PAYMENT RECEIVED · CONVERT" dabao.
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <Button type="button" onClick={onClose} variant="outline" className="rounded-sm border-slate-600 text-slate-300 hover:bg-slate-800 h-11">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy || (isOnline && amt > 0 && !showQR)}
              className="btn-industrial rounded-sm h-11 px-6 font-heading tracking-widest"
              data-testid="convert-submit"
            >
              {busy ? "CONVERTING…" : (isOnline && amt > 0 ? "PAYMENT RECEIVED · CONVERT" : "CONVERT")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">{label.toUpperCase()}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
